import { useCallback, useRef } from 'react';
import { useCreateAgentStatusChangeMutation } from 'services';
import { AGENT_STATUS } from 'utils';

interface StatusChangeParams {
  userId: number;
  extensionId: number | string;
  previousState?: string;
  nextState: string;
  reasonId?: number;
}

export const useAgentStatusManager = () => {
  const [createAgentStatusChange] = useCreateAgentStatusChangeMutation();

  const lastStatusChangeRef = useRef<{
    timestamp: number;
    previousState?: string;
    nextState?: string;
    userId?: number;
    extensionId?: number | string;
  }>({ timestamp: 0 });

  const pendingChangesRef = useRef<Set<string>>(new Set());

  const createStatusChange = useCallback(async (params: StatusChangeParams) => {
    const now = Date.now();
    const { userId, extensionId, previousState, nextState } = params;

    // Ensure extensionId is string for API call
    const extensionIdStr = typeof extensionId === 'number' ? extensionId.toString() : extensionId;

    // Skip if no valid previous state is provided
    if (!previousState || previousState === 'UNKNOWN') {
      console.log('Skipping status change - no valid previous state:', { previousState, nextState });
      return;
    }

    // Create unique key for this status change
    const changeKey = `${userId}-${extensionIdStr}-${previousState}-${nextState}`;

    // Check if this exact change is already pending
    if (pendingChangesRef.current.has(changeKey)) {
      console.log('Status change already pending:', changeKey);
      return;
    }

    // Check for duplicate within time window
    if (
      now - lastStatusChangeRef.current.timestamp < 1000 &&
      lastStatusChangeRef.current.userId === userId &&
      lastStatusChangeRef.current.extensionId?.toString() === extensionIdStr &&
      lastStatusChangeRef.current.previousState === previousState &&
      lastStatusChangeRef.current.nextState === nextState
    ) {
      console.log('Skipping duplicate status change within time window:', params);
      return;
    }

    // Skip if no actual state change
    if (previousState === nextState) {
      console.log('Skipping status change - same state:', { previousState, nextState });
      return;
    }

    // Add to pending changes
    pendingChangesRef.current.add(changeKey);

    try {
      // Update tracking
      lastStatusChangeRef.current = {
        timestamp: now,
        userId,
        extensionId: extensionIdStr,
        previousState,
        nextState
      };

      await createAgentStatusChange({
        userId,
        extensionId: extensionIdStr,
        previousState,
        nextState,
        reasonId: params.reasonId
      }).unwrap();
      console.log('Status change created successfully:', params);
    } catch (error) {
      console.error('Error creating agent status change:', error);
      throw error;
    } finally {
      // Remove from pending changes
      pendingChangesRef.current.delete(changeKey);
    }
  }, [createAgentStatusChange]);

  const clearStatusChangeHistory = useCallback(() => {
    lastStatusChangeRef.current = { timestamp: 0 };
    pendingChangesRef.current.clear();
  }, []);

  return {
    createStatusChange,
    clearStatusChangeHistory
  };
};
